/**
 * DeferredRoute — a route component whose module loads after first paint.
 *
 * WHY (2026-09-09). A signed-out visitor to the home page was downloading and
 * parsing the blog, onboarding, the wallet and the subscription page before
 * the hero could paint, because App.tsx imports every route statically. It
 * imports them statically because the obvious alternative, React.lazy, is
 * broken in this tree: see the long note at App.tsx:61-215 and in
 * src/utils/useDeferredModule.ts. Four PRs in May 2026 removed code-splitting
 * to escape that stall, and the bytes have been growing ever since.
 *
 * This is the escape route that does not go through Suspense. `deferredRoute`
 * takes the same `() => import('...')` a lazy route would, and renders nothing
 * until the module resolves — no boundary, no thrown thenable, no lazy
 * payload, so the initial-mount retry path that stalled is never entered.
 *
 * USE IT FOR: routes a first-time visitor does not land on — authenticated
 * pages, secondary marketing pages.
 * DO NOT USE IT FOR: `/` or anything else that must be in the first paint.
 * Those imports stay static, and the pin in
 * tests/hooks/unauthedLandingResilient.test.ts enforces it for the landing
 * page and EnsureFixedAssets.
 */

import React from 'react';
import { useDeferredModule } from '../utils/useDeferredModule';

type ComponentModule = { default: React.ComponentType<Record<string, never>> };

/**
 * Wraps a dynamic import as a route element.
 *
 * @param loader   the same `() => import('./pages/Thing')` a lazy route takes
 * @param key      stable identity for the load (the route path reads well)
 * @param fallback rendered while the module is in flight; `null` by default,
 *                 which is right for a route the user just navigated to and
 *                 whose own shell paints immediately after
 */
export function deferredRoute(
  loader: () => Promise<ComponentModule>,
  key: string,
  fallback: React.ReactNode = null,
): React.FC {
  const Deferred: React.FC = () => {
    const { module, error } = useDeferredModule(loader, key);

    if (error) {
      // Never a blank page on a chunk that failed. useDeferredModule has
      // already retried the download and handed a chunk that is gone for good
      // to staleTabSelfHeal (since 2026-09-22 — before that this comment's
      // promise was not kept); what reaches here is left for the error boundary.
      throw error;
    }
    if (!module) return <>{fallback}</>;

    const Loaded = module.default;
    return <Loaded />;
  };
  Deferred.displayName = `Deferred(${key})`;
  return Deferred;
}

export default deferredRoute;
